import React, { useEffect, useRef } from 'react';

interface VoiceWaveformProps {
  audioElement?: HTMLAudioElement | null;
  mediaStream?: MediaStream | null;
  isActive: boolean;
  className?: string;
  barCount?: number;
}

const BAR_GAP = 6;
const TOP_COLOR = '#0ea5b7';
const BOTTOM_COLOR = '#174e67';
const IDLE_BASELINE = 0.06;

const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
  audioElement = null,
  mediaStream = null,
  isActive,
  className,
  barCount = 36,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<AudioNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const smoothedRef = useRef<number[]>(new Array(barCount).fill(0));

  useEffect(() => {
    let cancelled = false;
    const setup = async () => {
      const AudioCtor: typeof AudioContext | undefined =
        (window.AudioContext as typeof AudioContext) ||
        ((window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!AudioCtor) return;

      const ctx = new AudioCtor();
      if (cancelled) {
        try { await ctx.close(); } catch (_) { /* noop */ }
        return;
      }
      audioCtxRef.current = ctx;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.78;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));

      try {
        if (audioElement) {
          const src = ctx.createMediaElementSource(audioElement);
          sourceRef.current = src;
          src.connect(analyser);
          analyser.connect(ctx.destination);
        } else if (mediaStream) {
          const src = ctx.createMediaStreamSource(mediaStream);
          sourceRef.current = src;
          src.connect(analyser);
        }
      } catch (err) {
        console.warn('VoiceWaveform: audio source bağlanamadı', err);
      }
    };

    void setup();

    return () => {
      cancelled = true;
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      try { sourceRef.current?.disconnect(); } catch (_) { /* noop */ }
      try { analyserRef.current?.disconnect(); } catch (_) { /* noop */ }
      const ctx = audioCtxRef.current;
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      dataArrayRef.current = null;
      if (ctx && ctx.state !== 'closed') {
        ctx.close().catch(() => undefined);
      }
    };
  }, [audioElement, mediaStream, barCount]);

  useEffect(() => {
    if (isActive && audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => undefined);
    }
  }, [isActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const analyser = analyserRef.current;
      const data = dataArrayRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      const cssWidth = canvas.clientWidth;
      const cssHeight = canvas.clientHeight;
      if (canvas.width !== cssWidth * dpr || canvas.height !== cssHeight * dpr) {
        canvas.width = cssWidth * dpr;
        canvas.height = cssHeight * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const totalBars = barCount;
      const totalGap = BAR_GAP * (totalBars - 1);
      const barWidth = Math.max(2, (cssWidth - totalGap) / totalBars);
      const center = cssHeight / 2;
      const maxHalf = cssHeight * 0.42;

      const smoothed = smoothedRef.current;
      const decay = isActive ? 0.7 : 0.86;

      let sliceSize = 0;
      let amplitudes: number[] | null = null;
      const buildActiveFallback = () => {
        const now = performance.now() / 1000;
        const generated: number[] = [];
        for (let bar = 0; bar < totalBars; bar += 1) {
          const wave = Math.sin(now * 5.2 + bar * 0.78) * 0.55 + Math.sin(now * 2.7 + bar * 0.34) * 0.45;
          generated.push(0.26 + Math.abs(wave) * 0.44);
        }
        return generated;
      };

      if (isActive && analyser && data) {
        analyser.getByteFrequencyData(data as unknown as Uint8Array<ArrayBuffer>);
        sliceSize = Math.floor(data.length / totalBars) || 1;
        const rawAmplitudes: number[] = [];
        for (let bar = 0; bar < totalBars; bar += 1) {
          let sum = 0;
          for (let offset = 0; offset < sliceSize; offset += 1) {
            sum += data[bar * sliceSize + offset];
          }
          const avg = sum / sliceSize / 255;
          rawAmplitudes.push(avg);
        }

        const peak = Math.max(...rawAmplitudes);
        const energy = rawAmplitudes.reduce((sum, value) => sum + value, 0) / Math.max(1, rawAmplitudes.length);
        if (peak > 0.008 || energy > 0.004) {
          const energyBoost = Math.min(0.2, energy * 3);
          amplitudes = rawAmplitudes.map((value) => {
            const normalized = peak > 0 ? Math.pow(value / peak, 0.78) : 0;
            return Math.min(0.92, 0.1 + normalized * 0.58 + energyBoost);
          });
        } else {
          amplitudes = buildActiveFallback();
        }
      } else if (isActive) {
        amplitudes = buildActiveFallback();
      }

      for (let i = 0; i < totalBars; i += 1) {
        const target = amplitudes ? amplitudes[i] : IDLE_BASELINE;
        smoothed[i] = smoothed[i] * decay + target * (1 - decay);
        const amp = Math.max(IDLE_BASELINE, smoothed[i]);
        const half = Math.max(2, amp * maxHalf);

        const x = i * (barWidth + BAR_GAP);
        const top = center - half;
        const height = half * 2;

        const gradient = ctx.createLinearGradient(0, top, 0, top + height);
        gradient.addColorStop(0, TOP_COLOR);
        gradient.addColorStop(1, BOTTOM_COLOR);
        ctx.fillStyle = gradient;

        const radius = Math.min(barWidth / 2, 8);
        ctx.beginPath();
        ctx.moveTo(x + radius, top);
        ctx.lineTo(x + barWidth - radius, top);
        ctx.quadraticCurveTo(x + barWidth, top, x + barWidth, top + radius);
        ctx.lineTo(x + barWidth, top + height - radius);
        ctx.quadraticCurveTo(x + barWidth, top + height, x + barWidth - radius, top + height);
        ctx.lineTo(x + radius, top + height);
        ctx.quadraticCurveTo(x, top + height, x, top + height - radius);
        ctx.lineTo(x, top + radius);
        ctx.quadraticCurveTo(x, top, x + radius, top);
        ctx.closePath();
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [barCount, isActive]);

  return (
    <canvas
      ref={canvasRef}
      className={`w-full h-full block ${className || ''}`}
      role="presentation"
    />
  );
};

export default VoiceWaveform;
